// Phase 0092 — HR Executive Dashboard.
// Decision dashboard for Plant Head / HR Head. One round-trip via /full.
// Auto-refresh every 60 seconds. All values are backend-computed.

import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, DatePicker, Empty, List, Row, Space,
  Statistic, Table, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ReloadOutlined, RiseOutlined, FallOutlined, TrophyOutlined,
  WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import {
  executiveDashboardApi, type DepartmentHealth,
} from '@/features/hr/executiveDashboardApi'
import PageHeader from '@/shared/components/PageHeader'

dayjs.extend(relativeTime)

const { Text, Title, Paragraph } = Typography

const STATUS_COLOR: Record<string, string> = {
  GREEN: '#52c41a', AMBER: '#fa8c16', RED: '#cf1322', UNKNOWN: '#8c8c8c',
}

const STATUS_BG: Record<string, string> = {
  GREEN: '#f6ffed', AMBER: '#fff7e6', RED: '#fff1f0', UNKNOWN: '#fafafa',
}

function lowerIsBetterColor(value: number, warning: number, danger: number): string {
  if (value >= danger) return '#cf1322'
  if (value >= warning) return '#fa8c16'
  return '#52c41a'
}

function higherIsBetterColor(value: number, low: number, ok: number): string {
  if (value < low) return '#cf1322'
  if (value < ok) return '#fa8c16'
  return '#52c41a'
}

export default function HRExecutiveDashboardPage() {
  const navigate = useNavigate()
  const [onDate, setOnDate] = useState<Dayjs>(dayjs())

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['hr-exec-dashboard', onDate.format('YYYY-MM-DD')],
    queryFn: () => executiveDashboardApi.full(onDate.format('YYYY-MM-DD')),
    refetchInterval: 60_000,
  })

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="HR Executive Dashboard" subtitle="Loading…" />
      </>
    )
  }

  const k = data.summary.kpis
  const dept = data.departments
  const te = data.tasks_escalations
  const ls = data.studies_capa
  const pa = data.performance_awards
  const sc = data.skill_certification
  const auto = data.automation_health

  // RED / AMBER department roll-up
  const redCount = dept.filter((d) => d.status === 'RED').length
  const amberCount = dept.filter((d) => d.status === 'AMBER').length
  const greenCount = dept.filter((d) => d.status === 'GREEN').length

  const deptColumns: ColumnsType<DepartmentHealth> = [
    {
      title: 'Department',
      key: 'dept',
      width: 200,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.department_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.department_code}</Text>
        </Space>
      ),
    },
    { title: 'Sanctioned', dataIndex: 'sanctioned_strength', width: 100 },
    { title: 'Active', dataIndex: 'active_manpower', width: 80 },
    { title: 'Present', dataIndex: 'present_today', width: 80 },
    {
      title: 'Att %',
      dataIndex: 'attendance_pct',
      width: 90,
      render: (v: number) => (
        <Text style={{ color: higherIsBetterColor(v, 80, 90), fontWeight: 600 }}>
          {v.toFixed(1)}%
        </Text>
      ),
      sorter: (a, b) => a.attendance_pct - b.attendance_pct,
    },
    {
      title: 'Shortage',
      dataIndex: 'manpower_shortage',
      width: 90,
      render: (v: number) => v > 0
        ? <Text style={{ color: '#cf1322' }}>{v}</Text> : v,
    },
    {
      title: 'Cert %',
      dataIndex: 'certified_pct',
      width: 90,
      render: (v: number) => (
        <Text style={{ color: higherIsBetterColor(v, 50, 80) }}>
          {v.toFixed(0)}%
        </Text>
      ),
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue_tasks',
      width: 90,
      render: (v: number) => v > 0
        ? <Text style={{ color: '#fa8c16' }}>{v}</Text> : v,
    },
    {
      title: 'Escalations',
      dataIndex: 'escalations_open',
      width: 110,
      render: (v: number) => v > 0
        ? <Text style={{ color: '#cf1322' }}>{v}</Text> : v,
    },
    {
      title: 'CAPA',
      dataIndex: 'capa_open',
      width: 80,
      render: (v: number) => v > 0
        ? <Text style={{ color: '#fa8c16' }}>{v}</Text> : v,
    },
    {
      title: 'Score',
      dataIndex: 'avg_performance_score',
      width: 80,
      render: (v: number) => v > 0
        ? <Text strong>{v.toFixed(1)}</Text> : '—',
      sorter: (a, b) => a.avg_performance_score - b.avg_performance_score,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      fixed: 'right',
      render: (s: string) => (
        <Tag color={s === 'GREEN' ? 'green' : s === 'AMBER' ? 'orange' : 'red'}
              style={{ fontWeight: 600 }}>
          {s}
        </Tag>
      ),
      filters: [
        { text: 'RED', value: 'RED' },
        { text: 'AMBER', value: 'AMBER' },
        { text: 'GREEN', value: 'GREEN' },
      ],
      onFilter: (v, r) => r.status === v,
    },
  ]

  return (
    <>
      <PageHeader
        title="HR Executive Dashboard"
        subtitle="Plant Head decision view — manpower, attendance, skill, tasks, studies, performance, automation"
        extra={
          <Space>
            <DatePicker value={onDate} onChange={(d) => d && setOnDate(d)} />
            <Text type="secondary">
              as of {dayjs(data.summary.as_of).format('HH:mm')}
            </Text>
            <Button icon={<ReloadOutlined />} loading={isFetching}
                    onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {/* Executive top-line KPI cards */}
      <Title level={5} style={{ marginBottom: 8 }}>Executive Summary</Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Active employees" value={k.total_active_employees} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Present today" value={k.present_today}
                       valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Absent today" value={k.absent_today}
                       valueStyle={{ color: lowerIsBetterColor(k.absent_today, 10, 30) }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Attendance %"
              value={k.attendance_pct}
              precision={1}
              suffix="%"
              valueStyle={{ color: higherIsBetterColor(k.attendance_pct, 80, 90) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Manpower shortage"
              value={k.manpower_shortage_total}
              valueStyle={{ color: lowerIsBetterColor(k.manpower_shortage_total, 5, 20) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Critical vacancies"
              value={k.critical_post_vacancies}
              valueStyle={{ color: lowerIsBetterColor(k.critical_post_vacancies, 1, 5) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" hoverable
                onClick={() => navigate('/hr/escalation-inbox')}>
            <Statistic
              title="Open escalations"
              value={k.open_escalations}
              valueStyle={{ color: lowerIsBetterColor(k.open_escalations, 5, 20) }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Overdue tasks"
              value={k.overdue_tasks}
              valueStyle={{ color: lowerIsBetterColor(k.overdue_tasks, 10, 50) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" hoverable
                onClick={() => navigate('/hr/certification-expiry')}>
            <Statistic
              title="Certs expiring 30d"
              value={k.certifications_expiring_30d}
              valueStyle={{ color: lowerIsBetterColor(k.certifications_expiring_30d, 5, 15) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Uncertified critical"
              value={k.uncertified_critical_deployments}
              valueStyle={{ color: lowerIsBetterColor(k.uncertified_critical_deployments, 1, 5) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Open CAPA (studies)"
              value={k.open_capa_from_studies}
              valueStyle={{ color: lowerIsBetterColor(k.open_capa_from_studies, 5, 20) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Open grievances"
              value={k.open_grievances}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Avg daily score"
              value={k.avg_daily_performance_score}
              precision={1}
              valueStyle={{ color: higherIsBetterColor(k.avg_daily_performance_score, 50, 75) }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ background: STATUS_BG[auto.status] }}>
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Automation health</Text>
              <Tag color={auto.status === 'GREEN' ? 'green'
                          : auto.status === 'AMBER' ? 'orange'
                          : auto.status === 'RED' ? 'red' : 'default'}
                    style={{ fontSize: 14, fontWeight: 600, padding: '2px 12px' }}>
                {auto.status}
              </Tag>
            </Space>
          </Card>
        </Col>
        {k.top_department && (
          <Col xs={24} md={6}>
            <Card size="small" style={{ background: '#f6ffed' }}>
              <Space direction="vertical" size={0}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <TrophyOutlined /> Top department
                </Text>
                <Text strong>{k.top_department.department_name}</Text>
                <Text>Score: {k.top_department.avg_score.toFixed(1)}</Text>
              </Space>
            </Card>
          </Col>
        )}
        {k.bottom_department && (
          <Col xs={24} md={6}>
            <Card size="small" style={{ background: '#fff1f0' }}>
              <Space direction="vertical" size={0}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <FallOutlined /> Lowest department
                </Text>
                <Text strong>{k.bottom_department.department_name}</Text>
                <Text>Score: {k.bottom_department.avg_score.toFixed(1)}</Text>
              </Space>
            </Card>
          </Col>
        )}
      </Row>

      {/* Department health table */}
      <Card
        title={
          <Space>
            <Text strong>Department-wise HR Health</Text>
            <Tag color="green">{greenCount} GREEN</Tag>
            <Tag color="orange">{amberCount} AMBER</Tag>
            <Tag color="red">{redCount} RED</Tag>
          </Space>
        }
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Table<DepartmentHealth>
          rowKey="department_id"
          columns={deptColumns}
          dataSource={dept}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* Deep sections */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        {/* Manpower + attendance */}
        <Col xs={24} lg={12}>
          <Card size="small" title="Manpower & Attendance">
            <Row gutter={8}>
              <Col span={8}>
                <Statistic title="Sanctioned" value={data.manpower_attendance.sanctioned_strength_total} />
              </Col>
              <Col span={8}>
                <Statistic title="Active" value={data.manpower_attendance.total_active_employees} />
              </Col>
              <Col span={8}>
                <Statistic title="Shortage" value={data.manpower_attendance.manpower_shortage}
                            valueStyle={{ color: lowerIsBetterColor(data.manpower_attendance.manpower_shortage, 5, 20) }} />
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={6}>
                <Statistic title="Present" value={data.manpower_attendance.present_today}
                            valueStyle={{ color: '#52c41a', fontSize: 18 }} />
              </Col>
              <Col span={6}>
                <Statistic title="Absent" value={data.manpower_attendance.absent_today}
                            valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={6}>
                <Statistic title="Half-day" value={data.manpower_attendance.half_day_today}
                            valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={6}>
                <Statistic title="Missing punch"
                            value={data.manpower_attendance.missing_punch_count}
                            valueStyle={{
                              fontSize: 18,
                              color: lowerIsBetterColor(data.manpower_attendance.missing_punch_count, 5, 20),
                            }} />
              </Col>
            </Row>
            {data.manpower_attendance.manual_review_required_count > 0 && (
              <Alert
                type="warning" showIcon style={{ marginTop: 12 }}
                message={`${data.manpower_attendance.manual_review_required_count} attendance row(s) need manual review`}
                action={
                  <Button size="small"
                          onClick={() => navigate('/hr/attendance-derivation')}>
                    Resolve
                  </Button>
                }
              />
            )}
          </Card>
        </Col>

        {/* Skill + certification */}
        <Col xs={24} lg={12}>
          <Card size="small" title="Skill & Certification">
            <Row gutter={8}>
              <Col span={8}>
                <Statistic title="Certified rows" value={sc.certified_total} />
              </Col>
              <Col span={8}>
                <Statistic title="Expiring 30d" value={sc.expiring_30d}
                            valueStyle={{ color: lowerIsBetterColor(sc.expiring_30d, 5, 15) }} />
              </Col>
              <Col span={8}>
                <Statistic title="Expired" value={sc.expired}
                            valueStyle={{ color: lowerIsBetterColor(sc.expired, 1, 5) }} />
              </Col>
            </Row>
            <Row style={{ marginTop: 12 }}>
              <Col span={24}>
                <Text type="secondary">Top multi-skilled employees ({sc.multi_skilled_count} total):</Text>
                {sc.top_multi_skilled.length === 0 ? (
                  <Text type="secondary"> none yet.</Text>
                ) : (
                  <List
                    size="small"
                    style={{ marginTop: 8 }}
                    dataSource={sc.top_multi_skilled.slice(0, 5)}
                    renderItem={(m) => (
                      <List.Item>
                        <Space>
                          <Text strong>{m.emp_name}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{m.emp_code}</Text>
                          <Tag color="cyan">{m.skill_count} skills</Tag>
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Tasks + escalations */}
        <Col xs={24} lg={12}>
          <Card size="small" title="Task & Escalation Health">
            <Row gutter={8}>
              <Col span={6}>
                <Statistic title="Generated today" value={te.generated_today} />
              </Col>
              <Col span={6}>
                <Statistic title="Completed" value={te.completed_today}
                            valueStyle={{ color: '#52c41a' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Verified" value={te.verified_today}
                            valueStyle={{ color: '#1677ff' }} />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Completion %"
                  value={te.completion_pct_today}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: higherIsBetterColor(te.completion_pct_today, 50, 80) }}
                />
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={6}>
                <Statistic title="Overdue" value={te.overdue_total}
                            valueStyle={{ color: lowerIsBetterColor(te.overdue_total, 10, 50) }} />
              </Col>
              <Col span={6}>
                <Statistic title="L1" value={te.escalation_by_level[1]} />
              </Col>
              <Col span={6}>
                <Statistic title="L2" value={te.escalation_by_level[2]}
                            valueStyle={{ color: '#fa8c16' }} />
              </Col>
              <Col span={6}>
                <Statistic title="L3" value={te.escalation_by_level[3]}
                            valueStyle={{ color: '#cf1322' }} />
              </Col>
            </Row>
            {te.top_overdue_tasks.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Top overdue tasks:</Text>
                <List
                  size="small"
                  dataSource={te.top_overdue_tasks.slice(0, 5)}
                  renderItem={(t) => (
                    <List.Item>
                      <Space>
                        <Text>{t.title}</Text>
                        {t.due_at && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            due {dayjs(t.due_at).fromNow()}
                          </Text>
                        )}
                        {t.escalation_level > 0 && (
                          <Tag color="red">L{t.escalation_level}</Tag>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Card>
        </Col>

        {/* Live study + CAPA */}
        <Col xs={24} lg={12}>
          <Card size="small" title="Live Study & CAPA">
            <Row gutter={8}>
              <Col span={8}>
                <Statistic title="Completed today" value={ls.completed_today} />
              </Col>
              <Col span={8}>
                <Statistic title="Pending verification" value={ls.pending_verification}
                            valueStyle={{ color: ls.pending_verification > 0 ? '#fa8c16' : '#52c41a' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Below standard" value={ls.below_standard}
                            valueStyle={{ color: lowerIsBetterColor(ls.below_standard, 3, 10) }} />
              </Col>
            </Row>
            <Row gutter={8} style={{ marginTop: 12 }}>
              <Col span={8}>
                <Statistic title="Open CAPA" value={ls.open_capa}
                            valueStyle={{ color: lowerIsBetterColor(ls.open_capa, 5, 20) }} />
              </Col>
              <Col span={8}>
                <Statistic title="Overdue CAPA" value={ls.overdue_capa}
                            valueStyle={{ color: lowerIsBetterColor(ls.overdue_capa, 1, 5) }} />
              </Col>
              <Col span={8}>
                <Statistic title="Training reco's" value={ls.training_recommendations_open}
                            valueStyle={{ color: lowerIsBetterColor(ls.training_recommendations_open, 3, 10) }} />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Performance + awards */}
        <Col xs={24}>
          <Card size="small" title="Performance & Awards">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={6}>
                <Statistic title="Avg daily score" value={pa.avg_daily_score}
                            precision={1} />
                <Statistic title="Avg monthly score" value={pa.avg_monthly_score}
                            precision={1} style={{ marginTop: 12 }} />
              </Col>
              <Col xs={24} md={9}>
                <Text type="secondary">Top performers this month</Text>
                {pa.top_performers.length === 0 ? (
                  <Empty style={{ marginTop: 8 }} description="No monthly scores yet" />
                ) : (
                  <List
                    size="small" style={{ marginTop: 8 }}
                    dataSource={pa.top_performers.slice(0, 5)}
                    renderItem={(p) => (
                      <List.Item>
                        <Space>
                          {p.rank_overall && <Tag color="gold">#{p.rank_overall}</Tag>}
                          <Text strong>{p.emp_name}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{p.emp_code}</Text>
                          <Text>{p.total_score.toFixed(1)}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
              </Col>
              <Col xs={24} md={9}>
                <Text type="secondary">Bottom performers this month</Text>
                {pa.bottom_performers.length === 0 ? (
                  <Empty style={{ marginTop: 8 }} description="—" />
                ) : (
                  <List
                    size="small" style={{ marginTop: 8 }}
                    dataSource={pa.bottom_performers.slice(0, 5)}
                    renderItem={(p) => (
                      <List.Item>
                        <Space>
                          <Text strong>{p.emp_name}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>{p.emp_code}</Text>
                          <Text style={{ color: '#cf1322' }}>{p.total_score.toFixed(1)}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Automation health */}
        <Col xs={24}>
          <Card size="small"
                title={
                  <Space>
                    <Text strong>Automation health</Text>
                    <Tag color={auto.status === 'GREEN' ? 'green'
                                : auto.status === 'AMBER' ? 'orange'
                                : auto.status === 'RED' ? 'red' : 'default'}>
                      {auto.status}
                    </Tag>
                  </Space>
                }
                extra={
                  <Button size="small" onClick={() => navigate('/hr/automation')}>
                    Open Automation Control
                  </Button>
                }>
            <Row gutter={[8, 8]}>
              <Col xs={12} md={4}>
                <Statistic title="Scheduler jobs" value={auto.scheduler_jobs_active ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Open escalations" value={auto.open_escalations ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Att. derivation pending"
                            value={auto.attendance_derivation_pending ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Missing punch" value={auto.missing_punch_count_today ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Cert expiring" value={auto.certifications_expiring_30d ?? 0} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Failures 24h" value={auto.recent_failure_count ?? 0}
                            valueStyle={{ color: lowerIsBetterColor(auto.recent_failure_count ?? 0, 1, 5) }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  )
}
